"use client";

import React from "react";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SingleBlogPostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 h-10 w-32 animate-pulse rounded-md bg-muted"></div>
        <div className="mb-4 h-12 w-3/4 animate-pulse rounded-md bg-muted"></div>
        <div className="mb-8 h-6 w-1/2 animate-pulse rounded-md bg-muted"></div>
        <div className="mb-8 aspect-video w-full animate-pulse rounded-xl bg-muted"></div>
        <div className="mb-8 space-y-3">
          <div className="h-4 w-full animate-pulse rounded-md bg-muted"></div>
          <div className="h-4 w-full animate-pulse rounded-md bg-muted"></div>
          <div className="h-4 w-2/3 animate-pulse rounded-md bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center sm:px-6 lg:px-8">
        <p className="text-destructive">Ett fel uppstod när inlägget skulle laddas.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center sm:px-6 lg:px-8">
        <h1 className="font-serif text-2xl font-bold text-foreground">Inlägget hittades inte</h1>
        <p className="mt-4 text-muted-foreground">Det sökta inlägget existerar inte eller har tagits bort.</p>
        <Button asChild className="mt-8" variant="outline">
          <Link to="/InsightsPage">Tillbaka till insikter</Link>
        </Button>
      </div>
    );
  }

  const post = posts[0];
  const featuredImage = post.customFields?.featured_image || post.featuredImage;
  const authorName = post.customFields?.author_name || post.author?.name;
  const publishDate = post.customFields?.publish_date || post.date;

  const formattedDate = publishDate 
    ? new Date(publishDate).toLocaleDateString('sv-SE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <Button variant="ghost" asChild className="-ml-4 text-muted-foreground hover:text-foreground">
          <Link to="/InsightsPage">
            <ArrowLeft className="mr-2 size-4" />
            Tillbaka till insikter
          </Link>
        </Button>
      </div>

      <header className="mb-8">
        <h1 className="mb-4 font-serif text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl">
          {post.title}
        </h1>
        {post.customFields?.subtitle && (
          <p className="text-xl text-muted-foreground md:text-2xl">
            {post.customFields.subtitle}
          </p>
        )}
      </header>

      {featuredImage && (
        <div className="mb-10 overflow-hidden rounded-xl shadow-md">
          <AspectRatio ratio={16 / 9}>
            <img
              src={featuredImage}
              alt={post.title}
              className="size-full object-cover"
            />
          </AspectRatio>
        </div>
      )}

      {post.customFields?.summary && (
        <div className="prose prose-lg max-w-none text-foreground/80">
          <p className="whitespace-pre-wrap leading-relaxed">
            {post.customFields.summary}
          </p>
        </div>
      )}

      <Separator className="my-10" />

      {(authorName || formattedDate) && (
        <Card className="bg-secondary/50 border-none shadow-sm">
          <CardContent className="flex flex-col gap-6 p-6 sm:flex-row sm:items-center sm:justify-between">
            {authorName && (
              <div className="flex items-center gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <User className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Författare</p>
                  <p className="font-medium text-foreground">{authorName}</p>
                </div>
              </div>
            )}
            
            {formattedDate && (
              <div className="flex items-center gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Calendar className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Publicerad</p>
                  <time dateTime={publishDate} className="font-medium text-foreground">
                    {formattedDate}
                  </time>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {post.content && (
        <div className="mt-10">
          <div 
            className="prose prose-lg max-w-none text-foreground/90 prose-headings:font-serif prose-headings:text-foreground prose-a:text-primary hover:prose-a:text-primary/80"
            dangerouslySetInnerHTML={{ __html: post.content }} 
          />
        </div>
      )}
    </article>
  );
}

export default function SingleBlogPostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : { post_type: 'blog_post' };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-blog-post-template-section" className="bg-background py-16 md:py-24">
      <PostsProvider wp_query={wp_query}>
        <SingleBlogPostConsumer />
      </PostsProvider>
    </section>
  );
}